import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next/pages'
import { Breadcrumb, ContentListItem } from 'pcl-ui'
import React from 'react'
import { PageTitle } from '@/components/PageTitle'
import i18nextConfig from '@/next-i18next.config'
import { articles } from '@/state/articles'
import { getI18nPaths, getI18nProps } from '@/utils/getStatic'

interface Article {
  slug: string
}

const Articles = ({ articles: articleList }: { articles: Article[] }) => {
  const router = useRouter()
  const currentLocale = router.query.locale || i18nextConfig.i18n.defaultLocale
  const { t } = useTranslation(['common', 'articles-listing'])

  const homeHref =
    router.query.locale &&
    router.query.locale !== i18nextConfig.i18n.defaultLocale
      ? `/${router.query.locale}`
      : '/'
  const pageTitle = `${t('navItems.Articles')} — ${t('title')}`

  return (
    <div className="flex-1">
      <PageTitle>{pageTitle}</PageTitle>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <Breadcrumb
          className="mb-6"
          ariaLabel={t('Accessibility.breadcrumb')}
          items={[
            { label: t('navItems.Home'), href: homeHref },
            { label: t('navItems.Articles') },
          ]}
        />
        <h1 className="font-black text-[32px] tracking-[-0.03em] text-ink dark:text-dark-ink leading-[1.1] mb-2">
          {t('navItems.Articles')}
        </h1>
        <p className="text-sm font-semibold text-ink-secondary dark:text-dark-ink2 mb-8">
          {t('ArticlesIndex.subtitle')}
        </p>
        <div className="flex flex-col gap-3">
          {articleList.map((article) => {
            const href = `/${currentLocale}/${article.slug}`
            return (
              <ContentListItem
                key={article.slug}
                title={t(
                  `articles-listing:articles.${article.slug}.title` as never,
                )}
                excerpt={t(
                  `articles-listing:articles.${article.slug}.description` as never,
                )}
                ctaLabel={t('articles-listing:Article.ReadArticle')}
                href={href}
              />
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default Articles

export function getStaticPaths() {
  return { paths: getI18nPaths(), fallback: false }
}

export async function getStaticProps(ctx: { params: { locale: string } }) {
  return {
    props: {
      ...(await getI18nProps(ctx, ['common', 'articles-listing'])),
      articles,
    },
  }
}
